<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useMessage } from 'naive-ui'
import { useRemindersStore, type Reminder } from '../stores/reminders'
import { reminderWhenLabel } from '../composables/reminderWhenLabel'
import TodayCard from './TodayCard.vue'

// "Coming up" on Today: the next pending reminders, soonest first, each
// tickable in place. Times read as a clock time today and a weekday
// otherwise — the card is a glance, the Reminders page has the detail.
const PREVIEW_LIMIT = 4

const store = useRemindersStore()
const { reminders, loading, loadError } = storeToRefs(store)
const message = useMessage()

onMounted(() => {
  store.fetchReminders('pending').catch(() => {
    loadError.value = 'Could not load reminders'
  })
})

const upcoming = computed<Reminder[]>(() =>
  reminders.value
    .filter((r) => !r.done)
    .toSorted((a, b) => a.fire_at.localeCompare(b.fire_at))
    .slice(0, PREVIEW_LIMIT),
)

async function markDone(reminder: Reminder): Promise<void> {
  try {
    await store.toggleDone(reminder.id, true)
  } catch {
    message.error('Could not update the reminder')
  }
}
</script>

<template>
  <TodayCard title="Coming up" tone="life" link-to="/reminders" link-label="Reminders" testid="today-reminders">
    <p v-if="loadError" class="m-0 py-3 text-sm text-status-alert" data-testid="today-reminders-error">
      {{ loadError }}
    </p>
    <div v-else-if="loading && upcoming.length === 0" class="flex flex-col gap-3 py-2" aria-label="Loading reminders">
      <span class="h-3.5 w-2/3 rounded bg-border-divider" />
      <span class="h-3.5 w-1/2 rounded bg-border-divider" />
    </div>
    <p v-else-if="upcoming.length === 0" class="m-0 py-3 text-ink-muted" data-testid="today-reminders-empty">
      Nothing scheduled. Add one from Reminders.
    </p>
    <ul v-else class="m-0 list-none p-0">
      <li v-for="reminder in upcoming" :key="reminder.id" class="border-t border-border-divider first:border-t-0">
        <label class="flex min-h-11 cursor-pointer items-center gap-3" data-testid="today-reminder-row">
          <input
            type="checkbox"
            class="h-[18px] w-[18px] accent-life"
            :aria-label="`Mark done: ${reminder.message}`"
            @change="markDone(reminder)"
          />
          <span class="min-w-0 flex-1 truncate">{{ reminder.message }}</span>
          <span class="font-mono text-xs text-ink-faint">{{ reminderWhenLabel(reminder.fire_at) }}</span>
        </label>
      </li>
    </ul>
  </TodayCard>
</template>
